import React, { useRef, useEffect, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";
import { syncVideoState, formatTime, calculateDrift } from "../utils/videoSyncManager";

function WatchPartyPlayer({ videoUrl, isHost, socketManager, partyStatus, parentContainerRef }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [drift, setDrift] = useState(0);
  const heartbeatIntervalRef = useRef(null);
  const driftRef = useRef(0);

  // Initialize video element event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      if (isHost) {
        console.log("[WP-DEBUG] WatchPartyPlayer host <video> play → emitPlay", {
          currentTime: video.currentTime,
        });
        socketManager.emitPlay(video.currentTime);
      } else {
        console.log("[WP-DEBUG] WatchPartyPlayer <video> play (participant, no emit)");
      }
    };

    const handlePause = () => {
      setIsPlaying(false);
      if (isHost) {
        console.log("[WP-DEBUG] WatchPartyPlayer host <video> pause → emitPause", {
          currentTime: video.currentTime,
        });
        socketManager.emitPause(video.currentTime);
      }
    };

    const handleSeeked = () => {
      if (isHost) {
        console.log("[WP-DEBUG] WatchPartyPlayer host seeked → emitSeek", {
          currentTime: video.currentTime,
        });
        socketManager.emitSeek(video.currentTime);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("seeked", handleSeeked);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("seeked", handleSeeked);
    };
  }, [isHost, socketManager]);

  // Host: Send periodic heartbeat
  useEffect(() => {
    if (!isHost || !videoRef.current) return;

    heartbeatIntervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (video) {
        socketManager.emitHeartbeat(video.currentTime, !video.paused);
      }
    }, 5000); // Every 5 seconds

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isHost, socketManager]);

  // Participant: Listen to sync events
  useEffect(() => {
    if (isHost || !socketManager) return;

    console.log("[WP-DEBUG] WatchPartyPlayer participant sync listeners attached");

    let syncCheckInterval = null;

    const handleSyncState = (data) => {
      const video = videoRef.current;
      console.log("[WP-DEBUG] WatchPartyPlayer recv sync-state", {
        hasVideo: !!video,
        data,
      });
      if (!video) return;

      const { currentTime: targetTime, isPlaying: targetPlaying } = data;
      const currentDrift = calculateDrift(video.currentTime, targetTime);
      setDrift(currentDrift);
      driftRef.current = currentDrift;

      // Always sync playback state (playing/paused)
      if (targetPlaying && video.paused) {
        video.play().catch(err => console.error("Play error:", err));
      } else if (!targetPlaying && !video.paused) {
        video.pause();
      }

      // Sync time if drift exceeds threshold
      if (currentDrift > 2) {
        setIsSyncing(true);
        video.currentTime = targetTime;
        setTimeout(() => setIsSyncing(false), 1000);
      } else if (currentDrift > 0.5 && currentDrift <= 2) {
        // Minor drift - adjust playback speed slightly
        if (targetPlaying && !video.paused) {
          if (video.currentTime < targetTime) {
            video.playbackRate = 1.1; // Catch up
            setTimeout(() => { video.playbackRate = 1.0; }, 2000);
          } else if (video.currentTime > targetTime) {
            video.playbackRate = 0.9; // Slow down
            setTimeout(() => { video.playbackRate = 1.0; }, 2000);
          }
        }
      }
    };

    const handlePlay = (data) => {
      const video = videoRef.current;
      console.log("[WP-DEBUG] WatchPartyPlayer recv play", {
        hasVideo: !!video,
        data,
      });
      if (!video) return;
      const { currentTime: targetTime } = data;
      
      setIsSyncing(true);
      video.currentTime = targetTime;
      video.play().catch(err => console.error("Play error:", err));
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handlePause = (data) => {
      const video = videoRef.current;
      if (!video) return;
      const { currentTime: targetTime } = data;
      
      setIsSyncing(true);
      video.currentTime = targetTime;
      video.pause();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handleSeek = (data) => {
      const video = videoRef.current;
      if (!video) return;
      const { currentTime: targetTime } = data;
      
      setIsSyncing(true);
      video.currentTime = targetTime;
      setTimeout(() => setIsSyncing(false), 500);
    };

    // Additional periodic drift check every 3 seconds
    syncCheckInterval = setInterval(() => {
      const video = videoRef.current;
      if (!video || video.paused) return;

      // Request fresh sync state if we detect issues
      const currentDrift = driftRef.current;
      if (currentDrift > 1) {
        socketManager.requestSync();
      }
    }, 3000);

    socketManager.on("sync-state", handleSyncState);
    socketManager.on("play", handlePlay);
    socketManager.on("pause", handlePause);
    socketManager.on("seek", handleSeek);

    // Request initial sync state when joining
    setTimeout(() => socketManager.requestSync(), 500);

    return () => {
      if (syncCheckInterval) clearInterval(syncCheckInterval);
      socketManager.off("sync-state", handleSyncState);
      socketManager.off("play", handlePlay);
      socketManager.off("pause", handlePause);
      socketManager.off("seek", handleSeek);
    };
  }, [isHost, socketManager]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * video.duration;
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(!video.muted);
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    // Use parent container if provided (for chat overlay), otherwise use video element
    const elementToFullscreen = parentContainerRef?.current || videoRef.current;
    if (!elementToFullscreen) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      elementToFullscreen.requestFullscreen().catch(err => {
        console.error("Fullscreen request failed:", err);
      });
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative bg-slate-950 rounded-xl overflow-hidden border border-slate-700">
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full aspect-video bg-black"
        src={videoUrl}
        onError={(e) => console.error("Video error:", e)}
      />

      {/* Sync Indicator */}
      {isSyncing && (
        <div className="absolute top-4 left-4 bg-emerald-500/90 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Syncing...
        </div>
      )}

      {/* Host Badge */}
      {isHost && (
        <div className="absolute top-4 right-4 bg-violet-500/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
          HOST
        </div>
      )}

      {/* Participant Mode Notice */}
      {!isHost && (
        <div className="absolute top-4 right-4 bg-slate-800/90 text-slate-300 px-3 py-1.5 rounded-full text-xs">
          Following host
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4">
        {/* Progress Bar */}
        <div
          className="w-full h-1.5 bg-slate-700 rounded-full cursor-pointer mb-3 group"
          onClick={isHost ? handleSeek : undefined}
        >
          <div
            className="h-full bg-emerald-400 rounded-full relative group-hover:bg-emerald-300 transition"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition" />
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            {/* Play/Pause */}
            <button
              onClick={isHost ? togglePlayPause : undefined}
              disabled={!isHost}
              className={`p-2 rounded-full transition ${
                isHost
                  ? "hover:bg-white/20 active:scale-95"
                  : "opacity-50 cursor-not-allowed"
              }`}
              title={isHost ? "Play/Pause" : "Only host can control"}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>

            {/* Time */}
            <span className="text-sm font-medium">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {/* Drift Indicator (Participants) */}
            {!isHost && drift > 0.5 && (
              <span className="text-xs text-emerald-400">
                Drift: {drift.toFixed(1)}s
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Volume */}
            <div className="flex items-center gap-2 group">
              <button
                onClick={toggleMute}
                className="p-2 rounded-full hover:bg-white/20 transition"
              >
                {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-slate-600 rounded-full appearance-none cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
              />
            </div>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-full hover:bg-white/20 transition"
            >
              <Maximize size={18} />
            </button>
          </div>
        </div>

        {/* Status Message */}
        {partyStatus === "waiting" && (
          <div className="mt-2 text-center text-sm text-slate-400">
            Waiting for party to start...
          </div>
        )}
      </div>
    </div>
  );
}

export default WatchPartyPlayer;
